import React from 'react';
import PropTypes from 'prop-types';
import Favicon from 'react-favicon';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import auth from '../../services/appAuth';
import { SYSTEM_RESOURCE } from '../../constants/EndPoints';
import * as misc from '../../constants/Misc';

import './Login.scss';

function parseQuery(location) {
  const query = (location && location.query) || {};
  const search = (location && location.search) || '';
  const params = new URLSearchParams(search);

  return {
    oobCode: query.oobCode || params.get('oobCode') || '',
    continueUrl: query.continueUrl || params.get('continueUrl') || '',
  };
}

function safeContinuePath(rawTarget) {
  if (!rawTarget) {
    return '/login';
  }

  try {
    const url = new URL(rawTarget, window.location.origin);
    if (url.origin !== window.location.origin) {
      return '/login';
    }

    return url.pathname + url.search + url.hash;
  } catch (error) {
    return '/login';
  }
}

function redirectTo(path, props) {
  if (props && props.history && typeof props.history.replace === 'function') {
    props.history.replace(path);
    return;
  }

  window.location.replace(path);
}

function friendlyError(error, labels) {
  switch (error && error.code) {
    case 'auth/expired-action-code':
      return `${labels.linkNoun} expired. Please request a new one.`;
    case 'auth/invalid-action-code':
      return `${labels.linkNoun} is invalid or has already been used.`;
    case 'auth/user-disabled':
      return 'This account is currently disabled.';
    case 'auth/user-not-found':
      return 'We could not find the account for this link.';
    default:
      return (
        (error && error.message) ||
        `We could not complete this ${labels.actionNoun.toLowerCase()}.`
      );
  }
}

function EmailActionConfirmBase(props) {
  const {
    actionOperation,
    title,
    checkingText,
    applyingText,
    successMessage,
    confirmLabel,
    buildReadyCopy,
    buildSuccessCopy,
    buildMismatchMessage,
    labels,
  } = props;
  const [status, setStatus] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [actionCodeInfo, setActionCodeInfo] = React.useState(null);
  const location = (props && props.location) || window.location;
  const query = parseQuery(location);
  const continuePath = safeContinuePath(query.continueUrl);
  const sys = SYSTEM_RESOURCE;
  const systemName = process.env.REACT_APP_SYSTEM_NAME;
  const backgroundImagePath = require(`../../../images/system/${sys}/login_bg.png`);

  const mainStyle = {
    backgroundImage: `url(${backgroundImagePath})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  React.useEffect(
    function () {
      document.title = `${process.env.REACT_APP_SYSTEM_NAME || 'AQS'} - ${title}`;
    },
    [title]
  );

  React.useEffect(
    function () {
      let cancelled = false;

      async function loadActionCode() {
        if (!query.oobCode) {
          setStatus('error');
          setError(`${labels.linkNoun} is missing its action code.`);
          return;
        }

        setStatus('loading');
        setError('');

        try {
          const info = await auth.checkActionCode(query.oobCode);
          if (cancelled) {
            return;
          }

          if (!info || info.operation !== actionOperation) {
            setStatus('error');
            setError(buildMismatchMessage(info));
            return;
          }

          setActionCodeInfo(info);
          setStatus('ready');
        } catch (err) {
          if (cancelled) {
            return;
          }

          setStatus('error');
          setError(friendlyError(err, labels));
        }
      }

      loadActionCode();

      return function () {
        cancelled = true;
      };
    },
    [actionOperation, buildMismatchMessage, labels, query.oobCode]
  );

  const handleConfirm = async function () {
    if (status !== 'ready') {
      return;
    }

    setStatus('applying');
    setError('');

    try {
      await auth.applyActionCode(query.oobCode);
      setStatus('success');
    } catch (err) {
      setStatus('ready');
      setError(friendlyError(err, labels));
    }
  };

  return (
    <section className='page login-page mfa-confirm-removal-page'>
      <Favicon url={require(`./../../../images/system/${sys}/favicon_1.png`)} />

      <div className='main' style={mainStyle}>
        <div className='login-form form-horizontal'>
          <header>
            <div className='logo'>{systemName}</div>
            <div className='version'>
              {`v.`} <strong>{misc.versionNumber}</strong>
            </div>
          </header>

          <h2 className='mfa-confirm-title'>{title}</h2>

          {status === 'loading' && (
            <div className='mfa-confirm-loading'>
              <CircularProgress size={24} />
              <span>{checkingText}</span>
            </div>
          )}

          {status !== 'loading' && error && (
            <Alert severity='error' className='mfa-confirm-alert'>
              {error}
            </Alert>
          )}

          {status === 'ready' && (
            <>
              <p className='mfa-confirm-copy'>{buildReadyCopy(actionCodeInfo)}</p>
              <p className='mfa-confirm-copy mfa-confirm-muted'>
                This only happens after you click Confirm.
              </p>
              <div className='mfa-confirm-actions'>
                <Button type='button' variant='contained' onClick={handleConfirm}>
                  {confirmLabel}
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  onClick={() => redirectTo(continuePath, props)}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}

          {status === 'applying' && (
            <div className='mfa-confirm-loading'>
              <CircularProgress size={24} />
              <span>{applyingText}</span>
            </div>
          )}

          {status === 'success' && (
            <>
              <Alert severity='success' className='mfa-confirm-alert'>
                {successMessage}
              </Alert>
              <p className='mfa-confirm-copy'>{buildSuccessCopy(actionCodeInfo)}</p>
              <div className='mfa-confirm-actions'>
                <Button
                  type='button'
                  variant='contained'
                  onClick={() => redirectTo(continuePath, props)}
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <div className='mfa-confirm-actions'>
              <Button
                type='button'
                variant='contained'
                onClick={() => redirectTo(continuePath, props)}
              >
                Back to login
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

EmailActionConfirmBase.propTypes = {
  actionOperation: PropTypes.string.isRequired,
  applyingText: PropTypes.string.isRequired,
  buildMismatchMessage: PropTypes.func.isRequired,
  buildReadyCopy: PropTypes.func.isRequired,
  buildSuccessCopy: PropTypes.func.isRequired,
  checkingText: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
  labels: PropTypes.shape({
    actionNoun: PropTypes.string.isRequired,
    linkNoun: PropTypes.string.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    query: PropTypes.object,
    search: PropTypes.string,
  }),
  successMessage: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export default EmailActionConfirmBase;
